import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Input, Button, message, Steps, Divider, Radio, Space, Typography } from 'antd';
import { InfoIcon } from '@/components';
import { v4 as uuid } from 'uuid';
import './EventRegistrationModal.scss';
import { 
  createUser,
  createBooking
} from '@/utils/apiEndpoints';
import { updateTrip } from '@/utils/apiEndpoints';
import { Questions } from '@/views/CheckoutPage/questions';
import { generateUserDataFromEmail } from '@/utils/userDataGenerator';
import { MailOutlined, CheckCircleOutlined, UserOutlined, LockOutlined } from '@ant-design/icons';
import { Auth } from 'aws-amplify';
import { getInvites, getTripBookings } from '@/utils/apiEndpoints';
import { exit } from 'process';
import { s3Upload } from '@/utils/aws';

// const { Step } = Steps; // No longer needed with items prop

// Code input component
const CodeInput = ({ value = '', onChange, length = 6, onComplete }) => {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const inputsRef = useRef([]);

  useEffect(() => {
    const el = inputsRef.current[focusedIndex];
    if (el && typeof el.focus === 'function') {
      el.focus();
      try { el.select && el.select(); } catch (_) {}
    }
  }, [focusedIndex]);

  const handleChange = (index, digit) => {
    if (digit.length > 1) return; // single digit only
    if (digit && !/^\d$/.test(digit)) return; 

    const currentValue = value || '';
    const newValue = currentValue.split('');
    newValue[index] = digit;
    const newCode = newValue.join('');
    
    onChange(newCode);
    
    // auto-advance to next
    if (digit && index < length - 1) {
      setFocusedIndex(index + 1);
    } else if (!digit && index > 0) {
      // backspace on empty goes previous
      setFocusedIndex(index - 1);
    }
    
    // onComplete when full
    if ((newCode || '').replace(/\s/g, '').length === length && onComplete) {
      onComplete(newCode);
    }
  };

  const handleKeyDown = (index, e) => {
    const currentValue = value || '';
    if (e.key === 'Backspace' && !currentValue[index] && index > 0) {
      // backspace on empty
      setFocusedIndex(index - 1);
    } else if (e.key === 'ArrowLeft' && index > 0) {
      // arrow left
      e.preventDefault();
      setFocusedIndex(index - 1);
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      // arrow right
      e.preventDefault();
      setFocusedIndex(index + 1);
    } else if (e.key === 'Tab') {
      // allow default tab
      return;
    }
  };

  const handleFocus = (index) => {
    setFocusedIndex(index);
    const el = inputsRef.current[index];
    try { el && el.select && el.select(); } catch (_) {}
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange(pastedData);
    // focus last digit on paste
    const nextIndex = Math.min(pastedData.length, length - 1);
    setFocusedIndex(nextIndex);
    
    // trigger onComplete on paste
    if (pastedData.length === length && onComplete) {
      onComplete(pastedData);
    }
  };

  return (
    <div className="code-input-container" onPaste={handlePaste}>
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          type="text"
          maxLength={1}
          value={(value || '')[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onFocus={() => handleFocus(index)}
          className={`code-digit ${focusedIndex === index ? 'focused' : ''}`}
          inputMode="numeric"
          pattern="[0-9]*"
          ref={(el) => (inputsRef.current[index] = el)}
        />
      ))}
    </div>
  );
};

const EventRegistrationModal = ({ 
  visible, 
  onClose, 
  tripId, 
  trip,
  tripTitle, 
  tripDescription,
  tripImage,
  tripStartDate,
  tripEndDate,
  tripPrice,
  tripOrganization,
  onSuccess,
  onShowOtherLogin,
  skipLogin = false,
  currentStep: initialStep = 0
}) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [code, setCode] = useState('');
  const [isEmailLocked, setIsEmailLocked] = useState(false);
  const [form] = Form.useForm();
  const [loginForm] = Form.useForm();
  const [cognitoUser, setCognitoUser] = useState(null);
  const [authFlow, setAuthFlow] = useState(null); 
  
  // Booking steps state persisted in localStorage
  const [bookingStep, setBookingStep] = useState(0);
  const [attendees, setAttendees] = useState(() => {
    const saved = localStorage.getItem('booking_attendees');
    const savedValue = saved ? parseInt(saved) : 1;
    // If +1 is not enabled, always return 1
    if (!trip?.enableGuest) {
      return 1;
    }
    // If +1 is enabled, limit to 1 or 2
    return savedValue >= 1 && savedValue <= 2 ? savedValue : 1;
  });
  const [selectedAddons, setSelectedAddons] = useState(() => {
    const saved = localStorage.getItem('booking_addons');
    return saved ? JSON.parse(saved) : [];
  });

  const [flightAssistance, setFlightAssistance] = useState(() => {
    const saved = localStorage.getItem('booking_flight_assistance');
    return saved || '';
  });
  const [bookingForm] = Form.useForm();
  const [emailTemplateName, setEmailTemplateName] = useState('');
  const [emailTemplateSubject, setEmailTemplateSubject] = useState('');
  const [bookingId, setBookingId] = useState(null);

  const [guestData, setGuestData] = useState({
    name: '',
    email: '',
    relationship: ''
  });

  const [maxGroupSizeNumeric, setMaxGroupSizeNumeric] = useState(trip?.maxGroupSize === 500 ? 2 : trip?.maxGroupSize);

  // Booking options (rooms) state — format: [{ attendees, room, variant }]
  const [selectedRooms, setSelectedRooms] = useState([]);

  const roomCount = (variant) => {
    if (!selectedRooms?.length) return 0;
    return selectedRooms.reduce((acc, r) => r.variant?.id === variant.id ? acc + r.attendees : acc, 0);
  };
  const roomAddDisabled = (variant) => {
    if (variant.available - (variant.reserved || 0) <= 0) return true;
    if (attendees === 1 && selectedRooms?.length > 0) return true;
    if (attendees === 2 && selectedRooms?.length && (selectedRooms[0].attendees === 2 || selectedRooms.length >= 2)) return true;
    return false;
  };
  const roomAdd = (room, variant) => {
    if (!selectedRooms?.length) return [{ attendees: 1, room, variant }];
    if (selectedRooms[0].variant.id === variant.id) return [{ attendees: 2, room, variant }];
    return [...selectedRooms, { attendees: 1, room, variant }];
  };
  const roomRemove = (variantId) => {
    if (!selectedRooms?.length) return [];
    const existing = selectedRooms.find(r => r.variant?.id === variantId);
    if (!existing) return selectedRooms;
    if (existing.attendees > 1) return selectedRooms.map(r => r.variant?.id === variantId ? { ...r, attendees: r.attendees - 1 } : r);
    return selectedRooms.filter(r => r.variant?.id !== variantId);
  };

  // If the event has any paid components, ensure popup cannot be used
  const hasPayment = () => {
    // Using visible props: tripPrice may hint, but rely on selected add-ons too
    const baseIsPaid = !!tripPrice && Number(tripPrice) > 0;
    const paidAddonsSelected = selectedAddons.some((key) => {
      // Our demo add-ons list explicitly marks paid options by keys used below
      return ['transport', 'meal', 'guide'].includes(key);
    });
    return baseIsPaid || paidAddonsSelected;
  };

  // Whenever any paid option is detected, force checkout by disabling popup on trip
  useEffect(() => {
    if (!visible) return;
    if (hasPayment()) {
      try {
        // Persist setting on trip: isPopUpRegisterEnabled = false
        if (tripId) {
          updateTrip(tripId, { isPopUpRegisterEnabled: false });
        }
      } catch (_) {}
    }
  }, [visible]);

  // Reset attendees to 1 if +1 option is disabled
  useEffect(() => {
    if (!trip?.enableGuest && attendees > 1) {
      setAttendees(1);
    }
  }, [trip?.enableGuest, attendees]);

  // Persist booking state
  const saveBookingState = () => {
    localStorage.setItem('booking_attendees', attendees.toString());
    localStorage.setItem('booking_addons', JSON.stringify(selectedAddons));
    localStorage.setItem('booking_flight_assistance', flightAssistance);
  };

  const clearBookingState = () => {
    localStorage.removeItem('booking_attendees');
    localStorage.removeItem('booking_addons');
    localStorage.removeItem('booking_flight_assistance');
  };

  // Auto verify code
  const handleCodeComplete = async (completedCode) => {
    // Wait for 6 digits
    if (completedCode.length === 6) {
      setLoading(true);
      
      try {
        const savedPassword = localStorage.getItem('tempPassword');

        // --- AUTHENTICATION PHASE ---
        if (authFlow === 'signUp') {
          await Auth.confirmSignUp(email, completedCode);
          await Auth.signIn(email, savedPassword);
        } else if (authFlow === 'signIn') {
          if (!cognitoUser) throw new Error("Session invalid.");
          const loggedInUser = await Auth.sendCustomChallengeAnswer(cognitoUser, completedCode);
          if (loggedInUser.challengeName) throw new Error("Incorrect code.");
        } else {
          throw new Error("Unknown state.");
        }

        // --- SESSION SETUP ---
        await new Promise(resolve => setTimeout(resolve, 500));
        const session = await Auth.currentSession();
        localStorage.setItem('accessToken', session.getAccessToken().getJwtToken());
        localStorage.setItem('idToken', session.getIdToken().getJwtToken());

        // --- CONDITIONAL DATABASE SYNC (THE FIX) ---
        // Only create a new user record in our DB if they just signed up
        if (authFlow === 'signUp') {
            try {
              const generatedUserData = generateUserDataFromEmail(email);
              const userData = {
                email: email,
                username: generatedUserData.username,
                firstName: name.split(' ')[0],
                lastName: name.split(' ').slice(1).join(' ') || '',
                password: savedPassword,
                isEmailVerified: true,
                suppressWelcomeEmail: true,
                sendWelcomeEmail: false
              };
              await createUser(userData);
            } catch (createError) {
              console.warn('User sync/create skipped:', createError);
            }
        }
        // For existing 'signIn' users, we do NOTHING to their DB record here.
        // ----------------------------------------------------

        // --- SUCCESS ---
        message.success('Successfully logged in!');
        localStorage.removeItem('tempPassword');
        
        // --- PREFILL USER DATA FOR NEXT STEP ---
        // For existing users, we need to fetch their actual name
        if (authFlow === 'signIn') {
            const userInfo = await Auth.currentUserInfo();
            setUserData({
                email: userInfo.attributes?.email,
                firstName: userInfo.attributes?.given_name,
                lastName: userInfo.attributes?.family_name,
            });
        } else {
            // For new users, we can use the name from the form
            setUserData({
                email: email,
                firstName: name.split(' ')[0],
                lastName: name.split(' ').slice(1).join(' ') || '',
            });
        }
        
        setCurrentStep(2);

      } catch (error) {
        console.error("Verification Error:", error);
        
        // Specific Error Messaging
        if (error.code === 'ExpiredCodeException') {
          message.error('Code expired. Please request a new one.');
        } else if (error.code === 'CodeMismatchException' || error.message.includes('Incorrect')) {
          message.error('Incorrect code. Please try again.');
        } else {
          message.error(error.message || 'Verification failed.');
        }
        
        // Reset code so they can try again
        setCode('');
      } finally {
        setLoading(false);
      }
    }
  };

  // Перевіряємо авторизацію при відкритті модального вікна
  useEffect(() => {
    if (visible) {
      checkUserAuth();
    }
  }, [visible]);

  // Функція перевірки авторизації користувача
  const checkUserAuth = async () => {
    try {
      const session = await Auth.currentSession();
      if (session.isValid()) {
        // Користувач вже авторизований, переходимо до букінгу
        setCurrentStep(2);
        // Отримуємо інформацію про користувача
        const userInfo = await Auth.currentUserInfo();
        if (userInfo) {
          setEmail(userInfo.attributes?.email || '');
          setUserData({
            email: userInfo.attributes?.email || '',
            firstName: userInfo.attributes?.given_name || '',
            lastName: userInfo.attributes?.family_name || '',
            country: userInfo.attributes?.locale || ''
          });
        }
      } else {
        // Користувач не авторизований, починаємо з email
        setCurrentStep(0);
      }
    } catch (error) {
      // Користувач не авторизований, починаємо з email
      setCurrentStep(0);
    }
  };

  // Автоматично зберігаємо стан букінгу при зміні
  useEffect(() => {
    if (currentStep === 2) {
      saveBookingState();
    }
  }, [attendees, selectedAddons, flightAssistance, currentStep]);

  // Якщо skipLogin = true, починаємо з етапів бронювання
  useEffect(() => {
    if (skipLogin && currentStep === 0) {
      setCurrentStep(2);
    }
  }, [skipLogin, currentStep]);

  // Встановлюємо значення email в форму при зміні кроку
  useEffect(() => {
    if (currentStep === 1 && email) {
      form.setFieldsValue({ email: email });
    }
  }, [currentStep, email, form]);

  // Очищаємо код при зміні кроку
  useEffect(() => {
    if (currentStep === 1) {
      setCode('');
    }
  }, [currentStep]);

  const handleChangeEmail = () => {
    setIsEmailLocked(false);
    setCode('');
    // Очищаємо email поле для нового вводу
    setEmail('');
    // Очищаємо форми
    form.resetFields();
    loginForm.resetFields();
    // Повертаємося на перший крок для вводу нового email
    setCurrentStep(0);
  };

  const handleEmailSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    
    if (!email || !name) {
      console.log('Please enter your name and email.', { email, name });
      message.error('Please enter your name and email.');
      return;
    }

    setLoading(true);
    setIsEmailLocked(true);
    try {
      const generatedUserData = generateUserDataFromEmail(email);
      // Use saved temp password or generate new one
      let tempPassword = localStorage.getItem('tempPassword');
      if (!tempPassword) {
          tempPassword = generatedUserData.password;
          localStorage.setItem('tempPassword', tempPassword);
      }

      try {
        // --- Try 1: Sign Up (New User) ---
        await Auth.signUp({
          username: email.trim(),
          password: tempPassword,
          attributes: { email: email.trim(), name: name }
        });
        setCurrentStep(1);
        // Success = New User
        setAuthFlow('signUp');
        message.success('A verification code has been sent to your email.');
        
      } catch (signUpError) {
        if (signUpError.code === 'UsernameExistsException') {
          // --- Try 2: Sign In (Existing User) ---
          // This triggers the new Lambda backend
          const userObj = await Auth.signIn(email.trim());
          
          setCognitoUser(userObj); // <--- CAPTURE THIS OBJECT
          setAuthFlow('signIn');
          message.success('Welcome back! A login code has been sent.');
          setCurrentStep(1);
        } else {
          throw signUpError;
        }
      }
    } catch (error) {
      console.error(error);
      message.error(error.message || 'Error sending code.');
      setIsEmailLocked(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (values) => {
    // Just pass the value to the function we know works perfectly
    const verificationCode = values.code || code;
    if (verificationCode) {
      await handleCodeComplete(verificationCode);
    } else {
      message.error('Please enter the verification code');
    }
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      
      // Генеруємо дані користувача з email
      const generatedUserData = generateUserDataFromEmail(email);
      
      // Отримуємо збережений пароль
      const savedPassword = localStorage.getItem('tempPassword');
      
      // Очищаємо попередній код
      setCode('');
      
      try {
        // Спробуємо відправити код підтвердження для існуючого користувача
        await Auth.resendSignUp(email);
        message.success('Verification code sent again');
      } catch (resendError) {
        // Якщо не вдалося відправити код, створюємо нового користувача
        try {
          await Auth.signUp({
            username: email,
            password: savedPassword || generatedUserData.password,
            attributes: {
              'custom:firstName': generatedUserData.firstName,
              'custom:lastName': generatedUserData.lastName,
              'custom:username': generatedUserData.username,
            }
          });
          message.success('Verification code sent again');
        } catch (signUpError) {
          // Якщо створення не вдалося, спробуємо скинути пароль
          await Auth.forgotPassword(email);
          message.success('Password reset code sent again');
        }
      }
    } catch (error) {
      console.error('Error resending code:', error);
      if (error.code === 'NotAuthorizedException' && error.message === 'Password attempts exceeded') {
        message.error('Too many failed login attempts. Please wait a few minutes before trying again.');
      } else {
        message.error('Error resending verification code');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (values) => {
    // console.log('handleLogin', values);
    const emailVal = values.email?.trim();
    if (!emailVal) {
      message.error('Please enter your email');
      return;
    }

    setLoading(true);
    // CRITICAL: Update state so the NEXT step (Code Verification) knows the email
    setEmail(emailVal);

    try {
      // 1. Trigger the Backend Lambda (CreateAuthChallenge)
      // This sends the OTP to the user's email
      const userObj = await Auth.signIn(emailVal);

      // 2. Success: Capture the session object
      setCognitoUser(userObj); 
      setAuthFlow('signIn');   // Mark this as an existing user flow
      
      message.success('Login code sent to your email.');
      setCurrentStep(1);       // Move to Step 1 (Enter Code)

    } catch (error) {
      console.error('Login Error:', error);

      if (error.code === 'UserNotFoundException') {
        // User does not exist -> Guide them to Registration
        message.error('Account not found. Please register to attend.');
        setIsLoginMode(false); // Switch UI back to Register mode
      } 
      else if (error.code === 'UserNotConfirmedException') {
        // Exists but email not verified -> Resend verification code
        try {
          await Auth.resendSignUp(emailVal);
          setAuthFlow('signUp'); // Treat as new user verification
          setCurrentStep(1);
          message.success('Account not verified. A new verification code has been sent.');
        } catch (resendErr) {
          message.error('Error sending code. Please try registering again.');
        }
      } 
      else {
        message.error(error.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLoginCodeSubmit = async (values) => {
    const verificationCode = values.code || code;
    
    if (verificationCode && verificationCode.length === 6) {
      // Use the exact same robust logic
      await handleCodeComplete(verificationCode);
    } else {
      message.error('Please enter the full 6-digit verification code');
    }
  };

  const handleClose = () => {
    try {
      if (currentStep !== 3) {
        setCurrentStep(skipLogin ? 2 : 0);
        setEmail('');
        setLoading(false);
        setUserData(null);
        setIsLoginMode(false);
        setCode('');
        setIsEmailLocked(false);
        setBookingStep(0);
        form.resetFields();
        loginForm.resetFields();
        bookingForm.resetFields();
        localStorage.removeItem('tempPassword');
      } else {
        clearBookingState();
        setCurrentStep(skipLogin ? 2 : 0);
        setEmail('');
        setLoading(false);
        setUserData(null);
        setIsLoginMode(false);
        setCode('');
        setIsEmailLocked(false);
        setBookingStep(0);
        setAttendees(1);
        setSelectedAddons([]);
        setFlightAssistance('');
        setSelectedRooms([]);
        form.resetFields();
        loginForm.resetFields();
        bookingForm.resetFields();
        localStorage.removeItem('tempPassword');
      }
    } catch (_) {}
    onClose();
  };

  const renderStepContent = () => {
    if (currentStep === 0) {
      return isLoginMode ? renderLoginStep() : renderEmailStep();
    } else if (currentStep === 1) {
      return isLoginMode ? renderLoginCodeStep() : renderCodeStep();
    } else if (currentStep === 2) {
      return renderBookingSteps();
    } else if (currentStep === 3) {
      return renderSuccessStep();
    } else if (currentStep === 4) {
      return renderAlreadyRegisteredStep();
    }
    return null;
  };

  const renderEmailStep = () => (
    <div className="email-section">
      {/* Event Information */}
      <div className="event-info-above">
        <div className="event-title">{tripTitle || 'Event Registration'}</div>
        <div className="host-info-above">
          <div className="host-logo-above">
            {tripOrganization?.avatarUrl ? (
              <div className="organization-avatar">
                <img src={tripOrganization.avatarUrl} alt={tripOrganization.name} />
              </div>
            ) : (
              <div className="lightbulb-icon">💡</div>
            )}
            
          </div>
          <div className="host-text-above">
            Hosted by {tripOrganization?.name || 'Organization'}
          </div>
        </div>
      </div>
      
      {!isLoginMode ? (
        // Registration form
        <Form form={form} onFinish={handleEmailSubmit} layout="vertical">
          <div className="name-input-group">
            <Form.Item
              name="fullName"
              rules={[
                { required: true, message: 'Please enter your name' }
              ]}
            >
              <Input 
                placeholder="Enter your name" 
                value={name}
                onChange={(e) => {
                  // console.log('name changed', { name: e.target.value });
                  setName(e.target.value)
                }}
                size="large"
                disabled={isEmailLocked}
              />
            </Form.Item>
          </div>
          <div className="email-input-group">
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input 
                placeholder="Enter your email" 
                size="large"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isEmailLocked}
              />
            </Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              className="email-code-btn"
              disabled={isEmailLocked}
            >
              Email code
            </Button>
          </div>
          <div className="login-options-link" onClick={() => {
            if (onShowOtherLogin) {
              onShowOtherLogin();
            } else {
              setIsLoginMode(true);
            }
          }}>
            Use other login options
          </div>
        </Form>
      ) : (
        // Login form
        <Form form={loginForm} onFinish={handleLogin} layout="vertical">
          <div className="email-input-group">
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input 
                placeholder="Enter your email" 
                size="large"
                disabled={isEmailLocked}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              className="email-code-btn"
              disabled={isEmailLocked}
            >
              Email code
            </Button>
          </div>
          <div className="login-options-link" onClick={() => setIsLoginMode(false)}>
            Use other login options
          </div>
        </Form>
      )}
    </div>
  );

  const renderCodeStep = () => (
    <div className="verification-section">
      {/* Event Information */}
      <div className="event-info-above">
        <div className="event-title">{tripTitle || 'Event Registration'}</div>
        <div className="host-info-above">
          <div className="host-logo-above">
            {tripOrganization?.avatarUrl ? (
              <div className="organization-avatar">
                <img src={tripOrganization.avatarUrl} alt={tripOrganization.name} />
              </div>
            ) : (
              <div className="lightbulb-icon">💡</div>
            )}
            
          </div>
          <div className="host-text-above">
            Hosted by {tripOrganization?.name || 'Organization'}
          </div>
        </div>
      </div>
      
      <Form form={form} onFinish={isLoginMode ? handleLoginCodeSubmit : handleCodeSubmit} layout="vertical">
        {!isEmailLocked && (
          <div className="name-input-group">
            <Form.Item
              name="fullName"
              rules={[
                { required: true, message: 'Please enter your name' }
              ]}
            >
              <Input 
                placeholder="Enter your name" 
                size="large"
              />
            </Form.Item>
          </div>
        )}
        <div className="email-input-group">
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input 
              placeholder="Enter your email" 
              size="large"
              disabled={isEmailLocked}
            />
          </Form.Item>
          <Button 
            type={isEmailLocked ? "default" : "primary"}
            onClick={isEmailLocked ? handleChangeEmail : undefined}
            htmlType={isEmailLocked ? "button" : "submit"}
            loading={loading}
            className="email-code-btn"
          >
            {isEmailLocked ? 'Change email' : 'Email code'}
          </Button>
        </div>
        <div className="code-input-group">
          <CodeInput 
            value={code} 
            onChange={setCode} 
            length={6} 
            onComplete={handleCodeComplete}
          />
          <div className="verification-description">
            Please enter the 6 digit code just emailed to you
          </div>
        </div>
        
          
          

      </Form>
    </div>
  );

  const renderLoginStep = () => (
    <div className="email-section">
      {!isLoginMode ? (
        // Registration form
        <Form form={form} onFinish={handleEmailSubmit} layout="vertical">
          <div className="email-input-group">
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input 
                placeholder="Enter your email" 
                size="large"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isEmailLocked}
              />
            </Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              className="email-code-btn"
              disabled={isEmailLocked}
            >
              Email code
            </Button>
          </div>
          <div className="login-options-link" onClick={() => {
            if (onShowOtherLogin) {
              onShowOtherLogin();
            } else {
              setIsLoginMode(true);
            }
          }}>
            Use other login options
          </div>
        </Form>
      ) : (
        // Login form
        <Form form={loginForm} onFinish={handleEmailSubmit} layout="vertical">
          <div className="email-input-group">
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input 
                placeholder="Enter your email" 
                size="large"
                disabled={isEmailLocked}
              />
            </Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              className="email-code-btn"
              disabled={isEmailLocked}
            >
              Email code
            </Button>
          </div>
          <div className="login-options-link" onClick={() => setIsLoginMode(false)}>
            Use other login options
          </div>
        </Form>
      )}
    </div>
  );

  const renderLoginCodeStep = () => (
    <div className="verification-section">
      <Form form={form} onFinish={handleLoginCodeSubmit} layout="vertical">
        <div className="email-input-group">
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input 
              placeholder="Enter your email" 
              size="large"
              disabled={true}
              value={email}
            />
          </Form.Item>
          <Button 
            type="default"
            onClick={handleChangeEmail}
            className="email-code-btn"
          >
            Change email
          </Button>
        </div>
        <div className="code-input-group">
          <CodeInput 
            value={code} 
            onChange={setCode} 
            length={6} 
            onComplete={handleCodeComplete}
          />
          <div className="verification-description">
            Please enter the 6 digit code just emailed to you
          </div>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading}
            className="verify-btn"
          >
            Verify
          </Button>
        </div>
        <div className="action-links">
          <div className="resend-code" onClick={handleResendCode}>
            Resend code
          </div>
        </div>
      </Form>
    </div>
  );

  const renderBookingSteps = () => {
    return (
      <div className="booking-simple-layout">
        {/* Event information block */}
        <div className="event-location">
          <span className="location-text">{tripTitle}</span>
        </div>
        {trip && Array.isArray(trip?.addOns) && trip.addOns.length > 0 && (
          <div className="event-summary">
            <div className="summary-row">
              <span className="summary-label">Add-ons available:</span>
              <span className="summary-value">
                {trip.addOns.map(a => a.name).filter(Boolean).join(', ') || '—'}
              </span>
            </div>
          </div>
        )}

        {trip?.enableGuest && (
          <div className="attendees-section">
            <div className="attendees-title">
              Will you be bringing a +1?
            </div>
            <Radio.Group 
              onChange={(e) => setAttendees(e.target.value === 'yes' ? 2 : 1)} 
              value={attendees === 2 ? 'yes' : 'no'}
            >
              <Space direction="horizontal">
                <Radio value="no">No</Radio>
                <Radio value="yes">Yes</Radio>
              </Space>
            </Radio.Group>

            {/* Conditionally Render the +1 Fields */}
            {attendees === 2 && (
              <div className="plus-one-inputs">
                 <div>
                  <Typography.Text>
                    <b>Your guest details</b>
                    <InfoIcon text="Guest details help our hosts make better decisions on the composition of the group. The guest does not need to have a Tripsha account, but will need one to receive important notifications and participate in the group chat." />
                  </Typography.Text>
                </div>
                <div>
                    {/* <div style={{marginBottom: 4, fontSize: 12, fontWeight: 500}}>Guest Name</div> */}
                    <Input
                        id="guestName"
                        placeholder="Guest full name" 
                        value={guestData.name}
                        onChange={(e) => setGuestData({...guestData, name: e.target.value})}
                    />
                    <Input
                        id="guestEmail"
                        placeholder="Guest email address" 
                        value={guestData.email}
                        onChange={(e) => setGuestData({...guestData, email: e.target.value})}
                    />
                    <Input
                        id="guestRelationship"
                        placeholder="Relationship to you (e.g. Partner, Friend)" 
                        value={guestData.relationship}
                        onChange={(e) => setGuestData({...guestData, relationship: e.target.value})}
                    />
                </div>
              </div>
            )}
          </div>
        )}


        {/* Booking options (rooms) */}
        {Array.isArray(trip?.rooms) && trip.rooms.length > 0 && (
          <div className="booking-question">
            {trip.rooms.map((room) => {
              const visibleVariants = room.variants.filter(v => v['option-visibility']);
              if (!visibleVariants.length) return null;
              return (
                <div key={room.id}>
                  <div className="question-text">{room.name}</div>
                  {visibleVariants.map((variant) => {
                    const available = variant.available - (variant.reserved || 0);
                    const soldOut = available <= 0;
                    const count = roomCount(variant);
                    return (
                      <div key={variant.id} className="popup-booking-variant">
                        <div className="popup-booking-variant-details">
                          <div className="popup-booking-variant-name">{variant.name}</div>
                          {room['show-availability'] !== false && (
                            <div>
                              {soldOut
                                ? <Typography.Text className="error">Sold out</Typography.Text>
                                : <Typography.Text className="success">Available</Typography.Text>}
                            </div>
                          )}
                          {variant.cost ? <div className="popup-booking-variant-cost">${variant.cost}</div> : null}
                        </div>
                        <div className="counter-booking">
                          <Button disabled={count === 0} onClick={() => setSelectedRooms(roomRemove(variant.id))}>-</Button>
                          <div className="counter">{count}</div>
                          <Button disabled={soldOut || roomAddDisabled(variant)} onClick={() => setSelectedRooms(roomAdd(room, variant))}>+</Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {/* Dynamic add-ons from trip */}
        {Array.isArray(trip?.addOns) && trip.addOns.length > 0 && (
          <div className="booking-question">
            <div className="question-text">{trip.addOns[0]?.name}</div>
            <div className="checkbox-options">
              {trip.addOns.map((addOn) => (
                <div key={addOn.id || addOn._id} className="checkbox-option">
                  {addOn?.variants?.map((variant) => (
                    <label key={variant.id || variant._id} className="checkbox-option">
                      <input
                        type="checkbox"
                        onChange={async (e) => {
                          const key = `${addOn.id || addOn._id}:${variant.id || variant._id}`;
                          let next = selectedAddons;
                          if (e.target.checked) {
                            next = [...selectedAddons, key];
                          } else {
                            next = selectedAddons.filter(a => a !== key);
                          }
                          setSelectedAddons(next);
                          // If variant has cost > 0, force checkout
                          if (e.target.checked && Number(variant?.cost) > 0) {
                            try { if (tripId) await updateTrip(tripId, { isPopUpRegisterEnabled: false }); } catch (_) {}
                          }
                        }}
                      />
                      <span className="checkbox-label">
                        {addOn?.name}{variant?.name ? ` - ${variant.name}` : ''}
                        {Number(variant?.cost) > 0 ? ` ($${variant.cost})` : ' (Free)'}
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional questions from trip */}
        {Array.isArray(trip?.questions) && trip.questions.length > 0 && (
          <div className="booking-question">
            <div className="question-text">{trip.questions[1]?.question}</div>
            <Form form={bookingForm} layout="vertical">
              {/* Відображаємо питання так само як у чекауті */}
              <Questions trip={trip} type="booking" booking={{ rooms: selectedRooms, addOns: [] }} />
            </Form>
          </div>
        )}





        <div className="modal-actions">
          <Button 
            type="default" 
            onClick={onClose}
            className="cancel-btn"
          >
            Cancel
          </Button>
          <Button 
            type="primary" 
            onClick={handleFinalBooking}
            loading={loading}
            className="register-btn"
          >
            {trip?.finalButtonLabel || 'Register'}
          </Button>
        </div>
      </div>
    );
  };



  const handleFinalBooking = async () => {
    try {
      setLoading(true);
      let userIdForUpload;
      try {
        // --- FIX FOR PATH A (Authenticated User) ---
        // First, ensure we have a session.
        await Auth.currentSession();
        // Now, get the correct Identity Pool ID for S3.
        userIdForUpload = (await Auth.currentCredentials()).identityId;

      } catch (authError) {
        // --- FIX FOR PATH B (Unauthenticated User) ---
        console.error('Auth error:', authError);
        
        // The user is not logged in. We must create them AND sign them in
        // to generate a temporary Identity Pool ID.
        try {
          const generatedUserData = generateUserDataFromEmail(email);
          const savedPassword = localStorage.getItem('tempPassword');

          const userData = {
            email: email,
            username: generatedUserData.username,
            firstName: name.split(' ')[0],
            lastName: name.split(' ').slice(1).join(' ') || '',
            password: savedPassword || generatedUserData.password,
            isEmailVerified: true,
            phone: generatedUserData.phone,
            country: generatedUserData.country,
            timezone: generatedUserData.timezone,
            language: generatedUserData.language,
            displayName: generatedUserData.displayName,
            avatar: generatedUserData.avatar,
            bio: generatedUserData.bio,
            website: generatedUserData.website,
            socialLinks: generatedUserData.socialLinks,
            preferences: generatedUserData.preferences,
            suppressWelcomeEmail: true,
            sendWelcomeEmail: false
          };

          // Create the user in your backend and Cognito.
          const newUser = await createUser(userData);

          // IMPORTANT: After creating the user, we must sign them in to
          // establish a session and get their credentials.
          await Auth.signIn(userData.email, userData.password);
          
          // NOW that they are signed in, we can get the correct ID.
          userIdForUpload = (await Auth.currentCredentials()).identityId;

          if (!userIdForUpload) {
              throw new Error("Could not retrieve AWS credentials after sign-in.");
          }

        } catch (createError) {
            console.error('Error in user creation/sign-in flow:', createError);
            message.error('Could not create your user account.');
            setLoading(false);
            return;
        }
      }

      // Build dynamic payload according to existing checkout structure
      const bookingRequest = {
        tripId: tripId,
        attendees: attendees,
        paymentStatus: 'full',
        tripPaymentType: 'pay',
        flightAssistance: flightAssistance
      };
      // If +1 is selected, add the guest array to the payload
      if (attendees === 2) {
        // Basic validation (Optional: require name if +1 is selected)
        if (!guestData.name.trim()) {
            message.error("Please enter your guest's name");
            setLoading(false);
            return;
        }
        bookingRequest.guests = [{
          ...guestData,
          id: uuid()  // Generate unique ID for the backend
        }];
      }
      // Booking options (rooms)
      if (Array.isArray(selectedRooms) && selectedRooms.length > 0) {
        bookingRequest.rooms = selectedRooms;
      }

      // Convert selectedAddons keys into structures: [{ addOn, variant, attendees }]
      if (Array.isArray(selectedAddons) && selectedAddons.length > 0 && Array.isArray(trip?.addOns)) {
        const parsed = [];
        selectedAddons.forEach((key) => {
          const [addOnId, variantId] = key.split(':');
          const addOnObj = trip.addOns.find(a => `${a.id || a._id}` === addOnId);
          const variantObj = addOnObj?.variants?.find(v => `${v.id || v._id}` === variantId);
          if (addOnObj && variantObj) {
            parsed.push({ 
              addOn: addOnObj, 
              variant: {
                ...variantObj,
                // Explicitly ensure cost exists. Default to 0 if undefined/null.
                cost: (variantObj.cost === undefined || variantObj.cost === null) ? 0 : Number(variantObj.cost)
              }, 
              attendees: 1 
            });
          }
        });
        if (parsed.length > 0) bookingRequest.addOns = parsed;
      }
      
      // Questions
      try {
        const values = await bookingForm.validateFields();
        const formAnswers = values?.questions || {};
        // console.log('Questions form answers:', formAnswers,'values:', values);
        if (Array.isArray(trip?.questions) && trip.questions.length > 0) {
        // Use Promise.all to handle the asynchronous S3 upload
          const processedQuestions = await Promise.all(
            trip.questions.map(async (q) => {
              const answer = formAnswers?.[q.id];
              if (q.type === 'UploadFile' && answer && answer.length > 0) {
                    const uploadPromises = answer.map(fileItem => {
                        const fileToUpload = fileItem.originFileObj;
                        if (fileToUpload) {
                            return s3Upload(
                                fileToUpload,
                                userIdForUpload,
                                fileToUpload.name
                            );
                        }
                        return Promise.resolve(null);
                    });
                    const urls = await Promise.all(uploadPromises);
                    // The answer is now an array of URL strings
                    return { ...q, answer: urls.filter(url => url) };
              }
              return { ...q, answer: answer };
            })
          );

          // Assign the fully processed questions to the booking request.
          bookingRequest.questions = processedQuestions;
        }
      } catch (error) {
        if (error.errorFields) {
          // This is a validation error from antd.
          console.log("Form validation failed:", error.errorFields);
          message.error("Please answer all required questions.");
          // We don't need to do anything else. The user sees the error on the form.
          return; // Stop the function
        }
          // console.error("Error processing questions:", error);
      }

      const bookingResponse = await createBooking(bookingRequest);
      
      const newBooking = bookingResponse?.result;
      if (newBooking?._id) {
        setBookingId(newBooking._id);
      }
      
      setCurrentStep(3);
    } catch (error) {
              // Додаткова діагностика помилки
        if (error.response) {
          if (error.response.status === 403) {
            const errorData = error.response.data?.result;
            if (errorData?.type === 'BOOKING_ALREADY_EXISTS') {
            message.info('You are already registered for this event.');
            // Optionally show the "Already Registered" step in the modal
            setCurrentStep(3); // Or whatever step is appropriate
            setLoading(false);
              return;
            }
          } else if (error.response.status === 401) {
          message.error('Authentication failed. Please try again.');
          } else {
            message.error(`Error creating booking: ${error.response.data?.message || error.message}`);
          }
        } else {
          message.error('Error creating booking. Please try again.');
        }
    } finally {
      setLoading(false);
    }
  };

  const renderSuccessStep = () => (
    <div className="success-section">
      <div className="success-icon">✓</div>
      <div className="success-title">Submission successful</div>
      <div className="success-description">
        You have successfully submitted to {tripTitle || 'Event'}
      </div>
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '20px' }}>
        <Button 
          type="primary" 
          onClick={async () => {
            try {
              // Спроба отримати bookingId, якщо його ще немає у стейті
              let id = bookingId;
              if (!id && tripId) {
                const inv = await getInvites(tripId);
                const r = inv?.result;
                if (r) {
                  if (Array.isArray(r)) id = r[0]?._id || r[0]?.bookingId || id;
                  else id = r?._id || r?.bookingId || id;
                }
                if (!id) {
                  const tb = await getTripBookings(tripId);
                  const list = tb?.result || [];
                  if (Array.isArray(list) && list.length) id = list[0]?._id || id;
                }
              }
              handleClose();
              if (id) window.location.href = `/dashboard/booking-requests/${id}`;
              else window.location.href = `/dashboard/booking-requests${tripId ? `?tripId=${tripId}` : ''}`;
            } catch (_) {
              handleClose();
              window.location.href = `/dashboard/booking-requests${tripId ? `?tripId=${tripId}` : ''}`;
            }
          }}
        >
          View Booking Request
        </Button>
        <Button 
          type="primary" 
          onClick={async () => {
            try {
              // Спроба отримати bookingId, якщо його ще немає у стейті
              let id = bookingId;
              if (!id && tripId) {
                const inv = await getInvites(tripId);
                const r = inv?.result;
                if (r) {
                  if (Array.isArray(r)) id = r[0]?._id || r[0]?.bookingId || id;
                  else id = r?._id || r?.bookingId || id;
                }
                if (!id) {
                  const tb = await getTripBookings(tripId);
                  const list = tb?.result || [];
                  if (Array.isArray(list) && list.length) id = list[0]?._id || id;
                }
              }
              handleClose();
              if (id) window.location.href = `/dashboard/confirmed-events/${id}`;
              else window.location.href = `/dashboard/confirmed-events${tripId ? `?tripId=${tripId}` : ''}`;
            } catch (_) {
              handleClose();
              window.location.href = `/dashboard/confirmed-events${tripId ? `?tripId=${tripId}` : ''}`;
            }
          }}
        >
          Manage booking
        </Button>
      </div>
    </div>
  );

  const renderAlreadyRegisteredStep = () => (
    <div className="already-registered-section">
      <div className="already-registered-icon">⚠️</div>
      <div className="already-registered-title">Already Registered!</div>
      <div className="already-registered-description">
        You have already registered for this event. You can find your booking details on your dashboard.
      </div>
      <div className="event-info">
        <div className="event-title">{tripTitle || 'Event'}</div>
        <div className="event-organization">
          Hosted by {tripOrganization?.name || 'Organization'}
        </div>
      </div>
      <Button 
        type="primary" 
        onClick={handleClose}
        className="close-btn"
      >
        Close
      </Button>
    </div>
  );

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={800}
      className="event-registration-modal"
      destroyOnClose
    >
      {renderStepContent()}
    </Modal>
  );
};

export default EventRegistrationModal; 
